/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import { RegistryContexts } from "@app/models/constants/registry-constants";

/**
 * Examination preset.
 */
export interface DefaultExamination {
  /**
   * Examination name.
   */
  name: string;

  /**
   * Examination frequency (value + time unit).
   */
  frequency?: {
    value: number;
    unit: "year" | "month" | "week" | "day";
  };

  /**
   * Specific tags, eg: diabetes1, diabetes2, ...
   */
  tags: string[];

  /**
   * Extra sub exams contained into this
   */
  subExams?: string[];
}

export const DEFAULT_EXAMINATIONS_LIST: DefaultExamination[] = [
  {
    name: "Emoglobina glicata",
    frequency: { value: 3, unit: "month" },
    tags: [RegistryContexts.DIABETES1, RegistryContexts.DIABETES2],
  },
  {
    name: "Esami ematochimici",
    frequency: { value: 1, unit: "year" },
    tags: [RegistryContexts.DIABETES1, RegistryContexts.DIABETES2],
    subExams: [
      "Colesterolo totale",
      "Colesterolo HDL",
      "Colesterolo non-HDL",
      "Creatitnina",
      "Proteinuria",
      "Microalbuminura",
    ],
  },
  {
    name: "Celiachia",
    frequency: { value: 1, unit: "year" },
    tags: [RegistryContexts.DIABETES1],
  },
  {
    name: "TSH, T3, T4",
    frequency: { value: 1, unit: "year" },
    tags: [RegistryContexts.DIABETES1],
  },
  {
    name: "Esame urine completo",
    frequency: { value: 1, unit: "year" },
    tags: [RegistryContexts.DIABETES1, RegistryContexts.DIABETES2],
  },
  {
    name: "Elettrocardiogramma",
    frequency: { value: 5, unit: "year" },
    tags: [RegistryContexts.DIABETES1],
  },
  {
    name: "Elettrocardiogramma",
    frequency: { value: 1, unit: "year" },
    tags: [RegistryContexts.DIABETES2],
  },
  {
    name: "Visita oculistica + fondus oculi",
    frequency: { value: 1, unit: "year" },
    tags: [RegistryContexts.DIABETES1],
  },
  {
    name: "Visita oculistica + fondus oculi",
    frequency: { value: 2, unit: "year" },
    tags: [RegistryContexts.DIABETES2],
  },
  {
    name: "Fluorangiografia",
    frequency: { value: 5, unit: "year" },
    tags: [RegistryContexts.DIABETES1],
  },
  {
    name: "Visita neurologica",
    tags: [RegistryContexts.DIABETES1],
  },
  {
    name: "Valutazione neuropatia con monofilamento",
    frequency: { value: 1, unit: "year" },
    tags: [RegistryContexts.DIABETES2],
  },
  {
    name: "Ecografia della tiroide",
    frequency: { value: 2, unit: "year" },
    tags: [RegistryContexts.DIABETES1],
  },
  {
    name: "Valutazione dietologia",
    frequency: { value: 1, unit: "year" },
    tags: [RegistryContexts.DIABETES1],
  },
  {
    name: "Pressione sanguigna",
    frequency: { value: 1, unit: "month" },
    tags: [RegistryContexts.DIABETES2],
  },
  {
    name: "Peso in relazione al peso ponderale stimato per quel paziente",
    frequency: { value: 1, unit: "month" },
    tags: [RegistryContexts.DIABETES2],
  },
  {
    name: "Ecocardiogramma",
    frequency: { value: 5, unit: "year" },
    tags: [RegistryContexts.DIABETES2],
  },
  {
    name: "Ecocolor-doppler tronchi sovra-aortici",
    frequency: { value: 5, unit: "year" },
    tags: [RegistryContexts.DIABETES2],
  },
  {
    name: "Valutazione polsi periferici + Doppler",
    frequency: { value: 1, unit: "year" },
    tags: [RegistryContexts.DIABETES2],
  },
  {
    name: "TC",
    tags: [RegistryContexts.CDCD],
  },
  {
    name: "ecodoppler TSA",
    tags: [RegistryContexts.CDCD],
  },
  {
    name: "esami ematici",
    tags: [RegistryContexts.CDCD],
  },
  {
    name: "PET",
    tags: [RegistryContexts.CDCD],
  },
  {
    name: "RM",
    tags: [RegistryContexts.CDCD],
  },
];
